import { useEffect, useLayoutEffect, useSyncExternalStore, type RefObject } from "react";
import type { ChromeEdge } from "./chromeCss";
import {
  readChromeHidden,
  registerChromePiece,
  retainChromeScroll,
  subscribeChromeHidden,
} from "./chromeScrollEngine";

function readHiddenOnServer(): boolean {
  return false;
}

/**
 * Compact chrome stays off on AI Assist. That page pins its own conversation
 * header and composer; sliding the app bars would move them when the transcript
 * or the conversations list scrolls.
 */
export function compactChromeActive(
  showAppHeader: boolean,
  compact: boolean,
  pathname: string,
): boolean {
  return showAppHeader && compact && !pathname.startsWith("/ai-assist");
}

/**
 * On compact screens, scrolling hides the top and bottom chrome by the same
 * number of pixels and scrolling back reveals them. Desktop callers pass
 * `enabled: false` and leave the offset at zero.
 */
export function useCompactChrome(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    return retainChromeScroll();
  }, [enabled]);
}

/** Registers an in-flow block that slides away with the chrome on `edge`. */
export function useChromePiece(
  clipRef: RefObject<HTMLElement | null>,
  innerRef: RefObject<HTMLElement | null> | null,
  enabled: boolean,
  edge: ChromeEdge,
  fixedHeight?: number,
) {
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    const el = clipRef.current;
    if (!el) {
      return;
    }
    const inner = innerRef?.current ?? null;
    let height = fixedHeight ?? 0;
    if (fixedHeight == null && inner) {
      // The clip's own height rule can be 0 before the piece height is known.
      // Measuring under `auto` reads the content, then the rule takes over.
      const previous = el.style.height;
      el.style.height = "auto";
      height = inner.offsetHeight;
      el.style.height = previous;
    }
    return registerChromePiece(el, edge, height, fixedHeight == null ? inner : null);
  }, [clipRef, innerRef, enabled, edge, fixedHeight]);
}

/**
 * True while compact chrome is fully off screen. Hidden bars stay mounted for
 * the slide, so callers use this to set `inert` and drop pointer events.
 */
export function useChromeHidden(enabled: boolean): boolean {
  const hidden = useSyncExternalStore(subscribeChromeHidden, readChromeHidden, readHiddenOnServer);
  return enabled && hidden;
}
